import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AdminAuthContext } from '../features/auth';
import { useDistricts } from '../features/districts-admin';
import { extractDistrictIdForBreadcrumb } from './Breadcrumbs';

/**
 * Sprint BG-Menu (第 3 段): district admin 担当地区が 2 件以上ある場合に
 * ヘッダー上部に表示する地区切替セレクト。
 *
 * 仕様:
 *   - master role の場合は非表示 (サイドバー全地区から選択するため不要)
 *   - district role + 担当 1 件の場合も非表示 (切替不要)
 *   - 選択時は `/districts/:id` (overview) に navigate
 */
export function DistrictSwitcher({ ctx }: { ctx: AdminAuthContext }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const districtsState = useDistricts();

  const isMaster = ctx.role === 'master';

  const visible = useMemo(() => {
    if (isMaster) return [];
    if (districtsState.status !== 'ready') return [];
    const allow = new Set(ctx.districts);
    return districtsState.items.filter((d) => allow.has(d.id));
  }, [isMaster, districtsState, ctx.districts]);

  if (visible.length < 2) return null;

  const currentId = extractDistrictIdForBreadcrumb(pathname);

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <label htmlFor="district-switcher" className="text-xs text-slate-500">
        担当地区:
      </label>
      <select
        id="district-switcher"
        value={currentId ?? ''}
        onChange={(e) => {
          const id = e.target.value;
          if (id) navigate(`/districts/${id}`);
        }}
        className="min-w-0 max-w-full text-sm px-2 py-1 border border-slate-300 rounded bg-white"
      >
        <option value="" disabled>
          地区を選択
        </option>
        {visible.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
    </div>
  );
}
